import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../utils/api';
import entityConfig from '../../utils/entityConfig';
import { Link as RouterLink } from 'react-router-dom';
import TagService from '../../components/Tags/TagService';
import {
  Upload,
  Link as LinkIcon,
  File as FileIcon,
  Image as ImageIcon,
  ExternalLink,
  Download,
  Edit2,
  Trash2,
  X,
  Save,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  Tag,
  Paperclip,
  RefreshCw,
  Info,
  ShieldAlert,
  BookOpen,
  Layers,
  ListChecks,
  PieChart,
  Eye,
  FileText,
  Sparkles,
  Table,
  AlignLeft,
} from 'lucide-react';

// Static entity list - no dynamic loading to avoid duplicates
const STATIC_ENTITY_OPTIONS = [
  { value: '', label: 'All Entities' },
  { value: 'Global', label: 'Global' },
  { value: 'Activity', label: 'Activities' },
  { value: 'Customer', label: 'Customers' },
  { value: 'Knowledge', label: 'Knowledge Articles' },
  { value: 'Lead', label: 'Leads' },
  { value: 'Opportunity', label: 'Opportunities' },
  { value: 'SalesNote', label: 'Notes' },
  { value: 'SupportTicket', label: 'Support Tickets' },
];

const ENTITY_TYPE_ALIASES = {
  salesnote: 'SalesNote',
  salesnotes: 'SalesNote',
  'sales-note': 'SalesNote',
  'sales_notes': 'SalesNote',
  'sale note': 'SalesNote',
  'sale notes': 'SalesNote',
  'sale-note': 'SalesNote',
  'sale_notes': 'SalesNote',
  activity: 'Activity',
  activities: 'Activity',
  knowledge: 'Knowledge',
  knowledgearticle: 'Knowledge',
  knowledgearticles: 'Knowledge',
  customers: 'Customer',
  customer: 'Customer',
  leads: 'Lead',
  lead: 'Lead',
  opportunities: 'Opportunity',
  opportunity: 'Opportunity',
  supportticket: 'SupportTicket',
  supporttickets: 'SupportTicket',
  global: 'Global',
};

const ATTACHMENT_SOURCE_LABELS = {
  knowledge: 'Knowledge Center',
  attachments: 'CRM',
};

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];

function canonicalizeEntityTypeValue(value) {
  if (!value) return value;
  const key = value.toString().trim().toLowerCase();
  return ENTITY_TYPE_ALIASES[key] || value;
}

function getEntityOptions() {
  // Return static list - no dynamic loading or deduplication needed
  return STATIC_ENTITY_OPTIONS;
}

function normalizeTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags.filter(Boolean);
  }
  if (typeof tags === 'string') {
    if (!tags.trim()) return [];
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch {
      // Not JSON, fall through to comma split.
    }
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeAttachmentRecord(record) {
  if (!record) return null;
  const canonicalType = canonicalizeEntityTypeValue(record.EntityType || record.entityType);
  const tags = normalizeTags(record.Tags || record.tags);
  const source = (record.Source || record.source || 'attachments').toString().toLowerCase();
  const readOnly = record.ReadOnly ?? record.readOnly ?? source === 'knowledge';

  return {
    ...record,
    EntityType: canonicalType,
    Tags: tags,
    Source: source,
    source,
    ReadOnly: readOnly,
    readOnly,
  };
}

function formatBytes(bytes) {
  const numeric = Number(bytes);
  if (!numeric || Number.isNaN(numeric)) return '0 B';
  if (numeric === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.floor(Math.log(numeric) / Math.log(1024));
  const value = numeric / 1024 ** idx;
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[idx]}`;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleString();
}

function computeAttachmentStats(records = []) {
  const stats = {
    totalCount: 0,
    totalSize: 0,
    byType: {
      File: 0,
      Image: 0,
      URL: 0,
    },
    byEntity: {},
  };

  records.forEach((record) => {
    stats.totalCount += 1;
    stats.totalSize += Number(record.FileSize) || 0;

    const typeKey = record.AttachmentType || 'File';
    stats.byType[typeKey] = (stats.byType[typeKey] || 0) + 1;

    const entityKey = record.EntityType || 'Unassigned';
    stats.byEntity[entityKey] = (stats.byEntity[entityKey] || 0) + 1;
  });

  return stats;
}

function isImageAttachment(attachment) {
  if (!attachment) return false;
  if (attachment.AttachmentType === 'Image') return true;
  const fileName = attachment.OriginalFileName || '';
  return IMAGE_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));
}

function getFileIcon(attachment) {
  if (!attachment) return <FileIcon className="h-5 w-5 text-gray-400" />;
  if (attachment.AttachmentType === 'URL') {
    return <ExternalLink className="h-5 w-5 text-blue-500" />;
  }
  if (isImageAttachment(attachment)) {
    return <ImageIcon className="h-5 w-5 text-green-500" />;
  }
  return <FileIcon className="h-5 w-5 text-gray-500" />;
}

function getFileTypeIconPath(fileName) {
  if (!fileName) return null;
  const extension = fileName.toLowerCase().split('.').pop();
  
  const iconMap = {
    // PowerPoint
    'ppt': '/file/ppt.png',
    'pptx': '/file/ppt.png',
    // Word
    'doc': '/file/doc.png',
    'docx': '/file/doc.png',
    // PDF
    'pdf': '/file/pdf.png',
    // Excel
    'xls': '/file/xls.png',
    'xlsx': '/file/xls.png',
    'xlsm': '/file/xls.png',
  };
  
  return iconMap[extension] || null;
}

// Component to handle file type icon preview with error handling
const FileTypePreview = ({ attachment, onPreviewClick }) => {
  const [iconError, setIconError] = useState(false);
  const fileName = attachment.OriginalFileName || attachment.Title || '';
  const fileTypeIconPath = getFileTypeIconPath(fileName);
  
  return (
    <div 
      className="relative w-full h-48 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer overflow-hidden group"
      onClick={onPreviewClick}
    >
      {fileTypeIconPath && !iconError ? (
        <div className="flex flex-col items-center justify-center gap-3 p-4">
          <img
            src={fileTypeIconPath}
            alt={fileName}
            className="h-24 w-24 object-contain"
            onError={() => setIconError(true)}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            {fileName.split('.').pop()?.toUpperCase() || 'FILE'}
          </span>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2">
          {getFileIcon(attachment)}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {fileName || 'File'}
          </span>
        </div>
      )}
      {/* Overlay on hover */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
    </div>
  );
};

const AttachmentWorkspace = ({
  mode = 'global',
  entityType,
  entityId,
  onAttachmentChange,
  hideFilters = false,
}) => {
  const isGlobalMode = mode === 'global';
  const canonicalEntityType = useMemo(
    () => canonicalizeEntityTypeValue(entityType),
    [entityType]
  );

  const { t } = useTranslation();
  const { isDark } = useTheme();

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [entityOptions] = useState(() => getEntityOptions());
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    entityType: isGlobalMode ? '' : canonicalEntityType || '',
    attachmentType: '',
    source: '',
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalResults, setTotalResults] = useState(0);

  const [viewMode, setViewMode] = useState('table');

  const [imageUrls, setImageUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [failedImages, setFailedImages] = useState({}); // Track failed/not found images
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const blobUrlsRef = useRef(new Set()); // Track all blob URLs for cleanup

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    tags: '',
    entityType: isGlobalMode ? '' : canonicalEntityType || '',
    entityId: isGlobalMode ? '' : entityId || '',
  });

  const [urlForm, setUrlForm] = useState({
    url: '',
    title: '',
    description: '',
    tags: '',
    entityType: isGlobalMode ? '' : canonicalEntityType || '',
    entityId: isGlobalMode ? '' : entityId || '',
  });

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    tags: '',
    url: '',
    urlTitle: '',
    urlDescription: '',
    textContent: '',
  });
  const [extractingText, setExtractingText] = useState(false);
  const [textContentModalOpen, setTextContentModalOpen] = useState(false);
  const [viewingTextContent, setViewingTextContent] = useState(null);
  const [textContentViewMode, setTextViewMode] = useState('plain'); // 'plain' or 'table'

  const [editingAttachment, setEditingAttachment] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Selection state
  const [selectedAttachments, setSelectedAttachments] = useState(new Set());
  const [bulkTagModalOpen, setBulkTagModalOpen] = useState(false);
  const [bulkTags, setBulkTags] = useState([]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      entityType: isGlobalMode ? prev.entityType : canonicalEntityType || '',
    }));
    setUploadForm((prev) => ({
      ...prev,
      entityType: isGlobalMode ? prev.entityType : canonicalEntityType || '',
      entityId: isGlobalMode ? prev.entityId : entityId || '',
    }));
    setUrlForm((prev) => ({
      ...prev,
      entityType: isGlobalMode ? prev.entityType : canonicalEntityType || '',
      entityId: isGlobalMode ? prev.entityId : entityId || '',
    }));
    if (!isGlobalMode) {
      setPage(1);
    }
  }, [isGlobalMode, canonicalEntityType, entityId]);

  // No dynamic entity loading - using static list to avoid duplicates

  const entityDisplayLookup = useMemo(() => {
    const lookup = {};
    entityOptions.forEach(({ value, label }) => {
      if (!value) return;
      lookup[value.toString().toLowerCase()] = label;
    });
    return lookup;
  }, [entityOptions]);

  const resolveEntityLabel = useCallback(
    (value) => {
      if (!value) return t('attachments.unknownEntity', 'Unknown');
      const canonicalValue = canonicalizeEntityTypeValue(value);
      const directMatch = entityOptions.find(
        (option) => option.value === canonicalValue
      );
      if (directMatch) {
        return directMatch.label;
      }
      return canonicalValue || value;
    },
    [entityOptions, t]
  );

  const resolveEntityUrl = useCallback((type, id) => {
    if (!type || !id) return null;
    const canonicalType = canonicalizeEntityTypeValue(type);
    if (!canonicalType) return null;
    return `/admin/entities/${canonicalType.toString().toLowerCase()}/${id}`;
  }, []);

  const isKnowledgeEntity = useMemo(() => {
    const value = canonicalEntityType || filters.entityType;
    if (!value) return false;
    return canonicalizeEntityTypeValue(value).toString().toLowerCase() === 'knowledge';
  }, [canonicalEntityType, filters.entityType]);

  const loadAttachments = useCallback(async () => {
    if (!isGlobalMode && (!canonicalEntityType || !entityId)) {
      setAttachments([]);
      setTotalResults(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isGlobalMode) {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('limit', pageSize.toString());
        if (filters.entityType) params.set('entityType', filters.entityType);
        if (filters.attachmentType) params.set('attachmentType', filters.attachmentType);
        if (filters.search) params.set('search', filters.search.trim());
        if (filters.source) params.set('source', filters.source);

        const response = await fetch(
          `${api.baseURL}/api/attachments/all?${params.toString()}`,
          {
            method: 'GET',
            headers: api.getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load attachments');
        }

        const data = await response.json();
        const normalized = (data.attachments || [])
          .map(normalizeAttachmentRecord)
          .filter(Boolean);

        setAttachments(normalized);
        setTotalResults(
          Number.isFinite(data.total) ? data.total : normalized.length
        );
        if (Number.isFinite(data.limit) && data.limit > 0) {
          setPageSize(data.limit);
        }
      } else {
        const endpoint = `/attachments/${canonicalEntityType}/${entityId}`;
        const data = await api.get(endpoint);
        const normalized = (data.attachments || data || [])
          .map(normalizeAttachmentRecord)
          .filter(Boolean);

        setAttachments(normalized);
        setTotalResults(normalized.length);
      }
    } catch (err) {
      console.error('Failed to load attachments:', err);
      setError(err.message || 'Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [
    isGlobalMode,
    canonicalEntityType,
    entityId,
    page,
    pageSize,
    filters.entityType,
    filters.attachmentType,
    filters.search,
    filters.source,
  ]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const filteredAttachments = useMemo(() => {
    const searchTerm = filters.search.trim().toLowerCase();
    return attachments.filter((attachment) => {
      if (filters.attachmentType && attachment.AttachmentType !== filters.attachmentType) {
        return false;
      }

      if (isGlobalMode && filters.entityType) {
        const canonicalFilter = canonicalizeEntityTypeValue(filters.entityType);
        if (
          canonicalizeEntityTypeValue(attachment.EntityType) !== canonicalFilter
        ) {
          return false;
        }
      }

      if (filters.source) {
        const sourceValue = (attachment.Source || '').toLowerCase();
        if (sourceValue !== filters.source.toLowerCase()) {
          return false;
        }
      }

      if (searchTerm) {
        const candidates = [
          attachment.Title,
          attachment.Description,
          attachment.OriginalFileName,
          attachment.URL,
          attachment.Tags?.join(' '),
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase());

        if (!candidates.some((value) => value.includes(searchTerm))) {
          return false;
        }
      }

      return true;
    });
  }, [attachments, filters, isGlobalMode]);

  useEffect(() => {
    if (isDragOver && filteredAttachments.length === 0) {
      setIsDragOver(false);
    }
  }, [filteredAttachments.length, isDragOver]);

  useEffect(() => {
    if (!isGlobalMode) {
      setPage(1);
    }
  }, [isGlobalMode]);

  const stats = useMemo(
    () => computeAttachmentStats(filteredAttachments),
    [filteredAttachments]
  );

  const pageCount = useMemo(() => {
    if (!isGlobalMode) return 1;
    if (!totalResults) return 1;
    return Math.max(1, Math.ceil(totalResults / pageSize));
  }, [isGlobalMode, totalResults, pageSize]);

  const displayAttachments = useMemo(() => {
    if (!isGlobalMode) {
      return filteredAttachments;
    }
    return filteredAttachments;
  }, [filteredAttachments, isGlobalMode]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        [key]: key === 'entityType' ? canonicalizeEntityTypeValue(value) || '' : value,
      };
      return next;
    });
    // Clear selection when filters change
    clearSelection();
    if (isGlobalMode) {
      setPage(1);
    }
  };

  const handlePageChange = (direction) => {
    if (!isGlobalMode) return;
    setPage((prev) => {
      const next = Math.min(Math.max(1, prev + direction), pageCount);
      return next;
    });
  };

  // Selection handlers
  const toggleAttachmentSelection = (attachmentId) => {
    setSelectedAttachments((prev) => {
      const next = new Set(prev);
      if (next.has(attachmentId)) {
        next.delete(attachmentId);
      } else {
        next.add(attachmentId);
      }
      return next;
    });
  };

  const selectAllAttachments = () => {
    const allIds = new Set(displayAttachments.map((a) => a.AttachmentID));
    setSelectedAttachments(allIds);
  };

  const clearSelection = () => {
    setSelectedAttachments(new Set());
  };

  const isAttachmentSelected = (attachmentId) => {
    return selectedAttachments.has(attachmentId);
  };

  const isAllSelected = useMemo(() => {
    if (displayAttachments.length === 0) return false;
    return displayAttachments.every((a) => selectedAttachments.has(a.AttachmentID));
  }, [displayAttachments, selectedAttachments]);

  const isSomeSelected = useMemo(() => {
    return selectedAttachments.size > 0 && !isAllSelected;
  }, [selectedAttachments, isAllSelected]);

  const isAttachmentReadOnly = useCallback((attachment) => {
    if (!attachment) return false;
    if (attachment.readOnly || attachment.ReadOnly) return true;
    const sourceValue = (attachment.Source || attachment.source || '').toLowerCase();
    return sourceValue === 'knowledge';
  }, []);

  const getAttachmentSourceLabel = useCallback((attachment) => {
    if (!attachment) return null;
    const source = (attachment.Source || attachment.source || '').toLowerCase();
    if (ATTACHMENT_SOURCE_LABELS[source]) {
      return ATTACHMENT_SOURCE_LABELS[source];
    }
    if (!source) return null;
    return source.charAt(0).toUpperCase() + source.slice(1);
  }, []);

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!dropZoneRef.current?.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const openUploadModal = () => {
    setUploadForm((prev) => ({
      ...prev,
      file: null,
      title: '',
      description: '',
      tags: '',
      entityType: isGlobalMode ? '' : canonicalEntityType || '',
      entityId: isGlobalMode ? '' : entityId || '',
    }));
    setUploadModalOpen(true);
  };

  const openUrlModal = () => {
    setUrlForm({
      url: '',
      title: '',
      description: '',
      tags: '',
      entityType: isGlobalMode ? '' : canonicalEntityType || '',
      entityId: isGlobalMode ? '' : entityId || '',
    });
    setUrlModalOpen(true);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) return;
    const file = files[0];
    setUploadForm((prev) => ({
      ...prev,
      file,
      title: file.name,
      description: '',
      tags: '',
    }));
    setUploadModalOpen(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadForm((prev) => ({
      ...prev,
      file,
      title: file.name,
      description: '',
      tags: '',
    }));
    // Only open modal if it's not already open (for drag-and-drop or direct file input)
    if (!uploadModalOpen) {
      setUploadModalOpen(true);
    }
    // Reset the file input to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const validateUploadTarget = (targetEntityType, targetEntityId) => {
    if (!targetEntityType) {
      setError('Please select an entity type for this attachment.');
      return false;
    }
    if (
      canonicalizeEntityTypeValue(targetEntityType).toLowerCase() === 'knowledge'
    ) {
      setError('Knowledge article attachments are managed in the Knowledge Center.');
      return false;
    }
    if (
      canonicalizeEntityTypeValue(targetEntityType).toLowerCase() !== 'global' &&
      !targetEntityId
    ) {
      setError('Please provide an entity ID.');
      return false;
    }
    return true;
  };

  const handleFileUpload = async () => {
    if (!uploadForm.file) {
      setError('Please select a file to upload.');
      return;
    }

    const targetEntityType = isGlobalMode
      ? uploadForm.entityType
      : canonicalEntityType;
    const targetEntityId = isGlobalMode
      ? uploadForm.entityId || (canonicalizeEntityTypeValue(uploadForm.entityType) === 'Global' ? '0' : '')
      : entityId;

    if (!validateUploadTarget(targetEntityType, targetEntityId)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title || uploadForm.file.name);
      formData.append('description', uploadForm.description || '');
      formData.append('tags', uploadForm.tags || '');

      const uploadEndpoint = `/attachments/${canonicalizeEntityTypeValue(
        targetEntityType
      )}/${targetEntityId || 0}/upload`;

      const response = await fetch(`${api.baseURL}/api${uploadEndpoint}`, {
        method: 'POST',
        headers: (() => {
          const headers = api.getAuthHeaders();
          delete headers['Content-Type'];
          return headers;
        })(),
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to upload attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      setUploadModalOpen(false);
      setUploadForm((prev) => ({
        ...prev,
        file: null,
        title: '',
        description: '',
        tags: '',
      }));
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSubmit = async () => {
    const targetEntityType = isGlobalMode ? urlForm.entityType : canonicalEntityType;
    const targetEntityId = isGlobalMode ? urlForm.entityId : entityId;

    if (!validateUploadTarget(targetEntityType, targetEntityId)) {
      return;
    }

    if (!urlForm.url) {
      setError('Please provide a valid URL.');
      return;
    }

    try {
      new URL(urlForm.url);
    } catch {
      setError('URL format is invalid. Example: https://example.com/file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        `/attachments/${canonicalizeEntityTypeValue(targetEntityType)}/${targetEntityId || 0}/url`,
        {
          url: urlForm.url,
          title: urlForm.title,
          description: urlForm.description,
          tags: urlForm.tags,
        }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      setUrlModalOpen(false);
      setUrlForm({
        url: '',
        title: '',
        description: '',
        tags: '',
        entityType: isGlobalMode ? '' : canonicalEntityType || '',
        entityId: isGlobalMode ? '' : entityId || '',
      });
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to add URL attachment:', err);
      setError(err.message || 'Failed to add URL attachment');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (attachment) => {
    if (isAttachmentReadOnly(attachment)) {
      setError('This attachment can only be managed from its original module.');
      return;
    }
    setEditingAttachment(attachment);
    setEditForm({
      title: attachment.Title || '',
      description: attachment.Description || '',
      tags: attachment.Tags?.join(', ') || '',
      url: attachment.URL || '',
      urlTitle: attachment.URLTitle || '',
      urlDescription: attachment.URLDescription || '',
      textContent: attachment.TextContent || '',
    });
    setEditModalOpen(true);
  };

  const handleExtractText = async (attachment) => {
    if (!attachment) return;

    setExtractingText(true);
    setError(null);

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/${attachment.AttachmentID}/extract-text?fullText=false`,
        {
          method: 'POST',
          headers: api.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to extract text';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
      
      // Show success message
      if (data.extracted && data.textContent) {
        setError(null);
        // Optionally open edit modal with extracted text
        if (editingAttachment?.AttachmentID === attachment.AttachmentID) {
          setEditForm((prev) => ({ ...prev, textContent: data.textContent }));
        }
      }
    } catch (err) {
      console.error('Failed to extract text:', err);
      setError(err.message || 'Failed to extract text from attachment');
    } finally {
      setExtractingText(false);
    }
  };

  const handleEditSave = async () => {
    if (!editingAttachment) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/${editingAttachment.AttachmentID}`,
        {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(editForm),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to update attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      setEditModalOpen(false);
      setEditingAttachment(null);
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to save attachment:', err);
      setError(err.message || 'Failed to update attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attachment) => {
    if (!attachment) return;
    if (isAttachmentReadOnly(attachment)) {
      setError('This attachment can only be managed from its original module.');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete this attachment?'
    );
    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/${attachment.AttachmentID}`,
        {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to delete attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      setError(err.message || 'Failed to delete attachment');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (attachment) => {
    if (!attachment) return;

    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/download/${attachment.AttachmentID}`,
        {
          headers: api.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.OriginalFileName || attachment.Title || 'file';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(err.message || 'Failed to download attachment');
    }
  };

  // Handle tag changes for attachments - save via PUT endpoint
  const handleAttachmentTagChange = useCallback(async (attachmentId, newTags) => {
    if (!attachmentId) return;

    try {
      const attachment = attachments.find((a) => a.AttachmentID === attachmentId);
      if (!attachment) {
        console.warn('Attachment not found for tag update:', attachmentId);
        return;
      }

      // Convert tags array to comma-separated string for API
      const tagsString = Array.isArray(newTags) ? newTags.join(', ') : newTags;

      const response = await fetch(
        `${api.baseURL}/api/attachments/${attachmentId}`,
        {
          method: 'PUT',
          headers: {
            ...api.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: attachment.Title || '',
            description: attachment.Description || '',
            tags: tagsString,
            url: attachment.URL || '',
            urlTitle: attachment.URLTitle || '',
            urlDescription: attachment.URLDescription || '',
          }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to update attachment tags';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Reload attachments to reflect the change
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to update attachment tags:', err);
      setError(err.message || 'Failed to update tags');
    }
  }, [attachments, loadAttachments, onAttachmentChange]);

  // Bulk action handlers
  const handleBulkDelete = async () => {
    if (selectedAttachments.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedAttachments.size} attachment(s)?`
    );
    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const selectedIds = Array.from(selectedAttachments);
      const deletePromises = selectedIds.map(async (attachmentId) => {
        const response = await fetch(
          `${api.baseURL}/api/attachments/${attachmentId}`,
          {
            method: 'DELETE',
            headers: api.getAuthHeaders(),
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to delete attachment ${attachmentId}`);
        }
      });

      await Promise.all(deletePromises);
      
      clearSelection();
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setError(err.message || 'Failed to delete some attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkTagUpdate = async () => {
    if (selectedAttachments.size === 0) return;
    
    if (bulkTags.length === 0) {
      setError('Please add at least one tag to update');
      return;
    }

    // Close modal immediately to prevent TagService from staying active during update
    const selectedIds = Array.from(selectedAttachments);
    const tagsToApply = [...bulkTags]; // Store tags before closing modal
    
    setBulkTagModalOpen(false);
    setLoading(true);
    setError(null);
    setBulkTags([]); // Clear tags to prevent re-render issues
    
    try {
      const updateResults = [];
      
      for (const attachmentId of selectedIds) {
        const attachment = attachments.find((a) => a.AttachmentID === attachmentId);
        if (!attachment) {
          updateResults.push({ attachmentId, success: false, error: 'Attachment not found' });
          continue;
        }

        try {
          // Merge with existing tags and deduplicate
          const existingTags = Array.isArray(attachment.Tags) 
            ? attachment.Tags 
            : (attachment.Tags ? String(attachment.Tags).split(',').map(t => t.trim()).filter(Boolean) : []);
          
          const newTags = tagsToApply.length > 0 
            ? [...new Set([...existingTags, ...tagsToApply])] // Merge and deduplicate
            : existingTags;
          
          // Convert tags array to comma-separated string for API
          const tagsString = Array.isArray(newTags) ? newTags.join(', ') : String(newTags || '');
          
          // Add timeout to prevent hanging
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
          
          let response;
          try {
            response = await fetch(
              `${api.baseURL}/api/attachments/${attachmentId}`,
              {
                method: 'PUT',
                headers: {
                  ...api.getAuthHeaders(),
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  title: attachment.Title || '',
                  description: attachment.Description || '',
                  tags: tagsString,
                  url: attachment.URL || '',
                  urlTitle: attachment.URLTitle || '',
                  urlDescription: attachment.URLDescription || '',
                }),
                signal: controller.signal,
              }
            );
            clearTimeout(timeoutId);
          } catch (fetchErr) {
            clearTimeout(timeoutId);
            if (fetchErr.name === 'AbortError') {
              throw new Error('Request timeout after 30 seconds');
            }
            throw fetchErr;
          }

          if (!response.ok) {
            let errorMessage = `Failed to update tags for attachment ${attachmentId}`;
            try {
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                errorMessage = errorData.error || errorMessage;
              } else {
                const text = await response.text();
                errorMessage = text || errorMessage;
              }
            } catch {
              errorMessage = `${response.status}: ${response.statusText}`;
            }
            throw new Error(errorMessage);
          }

          updateResults.push({ attachmentId, success: true });
        } catch (err) {
          updateResults.push({ attachmentId, success: false, error: err.message });
        }
      }
      
      // Check if all updates succeeded
      const successCount = updateResults.filter(r => r.success).length;
      const failCount = updateResults.filter(r => !r.success).length;

      // Refresh attachments after updates
      if (successCount > 0) {
        clearSelection();
        await loadAttachments();
        if (onAttachmentChange) onAttachmentChange();
        
        if (failCount === 0) {
          setError(null);
        } else {
          setError(`Updated ${successCount} attachment(s), but ${failCount} failed.`);
        }
      } else if (failCount > 0) {
        setError(`Failed to update ${failCount} attachment(s).`);
      }
    } catch (err) {
      setError(err.message || 'Failed to update tags for some attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkExport = async () => {
    if (selectedAttachments.size === 0) return;

    setLoading(true);
    setError(null);

    try {
      const selectedIds = Array.from(selectedAttachments);
      const downloadPromises = selectedIds.map(async (attachmentId) => {
        const attachment = attachments.find((a) => a.AttachmentID === attachmentId);
        if (!attachment) return null;

        try {
          const response = await fetch(
            `${api.baseURL}/api/attachments/download/${attachmentId}`,
            {
              headers: api.getAuthHeaders(),
            }
          );

          if (!response.ok) {
            console.warn(`Failed to download attachment ${attachmentId}`);
            return null;
          }

          const blob = await response.blob();
          const fileName = attachment.OriginalFileName || attachment.Title || `attachment-${attachmentId}`;
          return { blob, fileName };
        } catch (err) {
          console.warn(`Error downloading attachment ${attachmentId}:`, err);
          return null;
        }
      });

      const files = await Promise.all(downloadPromises);
      const validFiles = files.filter(Boolean);

      if (validFiles.length === 0) {
        throw new Error('No files could be downloaded');
      }

      // Try to use JSZip if available, otherwise download files individually
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();

        validFiles.forEach(({ blob, fileName }) => {
          zip.file(fileName, blob);
        });

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = window.URL.createObjectURL(zipBlob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `attachments-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      } catch (zipError) {
        // Fallback: download files individually
        console.warn('JSZip not available, downloading files individually:', zipError);
        validFiles.forEach(({ blob, fileName }) => {
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = fileName;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(url);
        });
      }

      clearSelection();
    } catch (err) {
      console.error('Bulk export failed:', err);
      setError(err.message || 'Failed to export attachments');
    } finally {
      setLoading(false);
    }
  };

  const loadImageUrl = useCallback(
    async (attachment) => {
      if (!attachment || attachment.AttachmentType === 'URL') return null;
      const attachmentId = attachment.AttachmentID;
      if (!attachmentId) return null;
      
      // If image is already loaded, return it
      if (imageUrls[attachmentId]) return imageUrls[attachmentId];
      
      // If image already failed (especially 404), don't retry
      if (failedImages[attachmentId]) {
        console.warn(`Image ${attachmentId} already marked as failed, skipping retry`);
        return null;
      }
      
      // If image is currently loading, don't start another request
      if (loadingImages[attachmentId]) return null;

      setLoadingImages((prev) => ({ ...prev, [attachmentId]: true }));
      try {
        const response = await fetch(
          `${api.baseURL}/api/attachments/view/${attachmentId}`,
          {
            headers: api.getAuthHeaders(),
          }
        );
        
        if (!response.ok) {
          // Handle 404 (file not found) specifically
          if (response.status === 404) {
            console.warn(`File not found on server for attachment ${attachmentId}`);
            setFailedImages((prev) => ({ ...prev, [attachmentId]: 'not_found' }));
            throw new Error('File not found on server');
          }
          // Handle other errors
          throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        // Track blob URL for cleanup
        blobUrlsRef.current.add(blobUrl);
        setImageUrls((prev) => ({ ...prev, [attachmentId]: blobUrl }));
        return blobUrl;
      } catch (err) {
        console.error('Failed to fetch image preview:', err);
        // Mark as failed if it's a 404 or if we want to prevent retries
        if (err.message.includes('not found') || err.message.includes('404')) {
          setFailedImages((prev) => ({ ...prev, [attachmentId]: 'not_found' }));
        } else {
          // For other errors, we might want to retry, so don't mark as permanently failed
          setFailedImages((prev) => ({ ...prev, [attachmentId]: 'error' }));
        }
        return null;
      } finally {
        setLoadingImages((prev) => ({ ...prev, [attachmentId]: false }));
      }
    },
    [imageUrls, loadingImages, failedImages]
  );

  const openPreview = async (attachment) => {
    if (!attachment) return;
    if (isImageAttachment(attachment)) {
      const attachmentId = attachment.AttachmentID;
      
      // Check if file is already marked as not found
      if (failedImages[attachmentId] === 'not_found') {
        setError('File not found on server. The file is no longer available.');
        return;
      }
      
      // Check if image is already loaded (from gallery preview)
      if (imageUrls[attachmentId]) {
        // Image is already loaded, just open the modal
        setSelectedImage(attachment);
        setImageModalOpen(true);
        return;
      }
      
      // If not loaded yet, try to load it
      // Open modal first so user sees loading state
      setSelectedImage(attachment);
      setImageModalOpen(true);
      
      // Then try to load the image in the background
      try {
        const imageUrl = await loadImageUrl(attachment);
        
        // Check if it failed to load (especially 404)
        if (!imageUrl && failedImages[attachmentId] === 'not_found') {
          // Image failed to load, but modal is already open so user will see the error message
          return;
        }
        
        // If image loaded successfully, it will be in imageUrls state and modal will show it
        // The modal rendering checks imageUrls state, so it will update automatically
      } catch (err) {
        console.error('Failed to load image in preview:', err);
        // Error is already handled in loadImageUrl, modal will show appropriate message
      }
      return;
    }

    if (attachment.AttachmentType === 'URL' && attachment.URL) {
      window.open(attachment.URL, '_blank', 'noopener');
      return;
    }

    try {
      const attachmentId = attachment.AttachmentID;
      
      // Check if file is already marked as not found
      if (failedImages[attachmentId] === 'not_found') {
        setError('File not found on server. The file is no longer available.');
        return;
      }
      
      const response = await fetch(
        `${api.baseURL}/api/attachments/view/${attachmentId}`,
        {
          headers: api.getAuthHeaders(),
        }
      );
      
      if (!response.ok) {
        // Handle 404 (file not found) specifically
        if (response.status === 404) {
          setFailedImages((prev) => ({ ...prev, [attachmentId]: 'not_found' }));
          setError('File not found on server. The file is no longer available.');
          return;
        }
        throw new Error(`Failed to open attachment: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (err) {
      console.error('Preview failed:', err);
      // Mark as failed if it's a 404 or if we want to prevent retries
      const attachmentId = attachment.AttachmentID;
      if (err.message.includes('not found') || err.message.includes('404')) {
        setFailedImages((prev) => ({ ...prev, [attachmentId]: 'not_found' }));
      }
      setError(err.message || 'Unable to open attachment preview');
    }
  };

  useEffect(() => {
    if (!imageModalOpen) return undefined;
    const listener = (event) => {
      if (event.key === 'Escape') {
        setImageModalOpen(false);
        setSelectedImage(null);
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [imageModalOpen]);

  useEffect(() => {
    // Only revoke blob URLs on component unmount
    return () => {
      blobUrlsRef.current.forEach((url) => {
        if (url && url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
      blobUrlsRef.current.clear();
    };
  }, []); // Empty dependency array - only run cleanup on unmount

  const tableHeaderClass = isDark
    ? 'bg-gray-800 text-gray-200'
    : 'bg-gray-50 text-gray-600';
  const cardBackground = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const panelBackground = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const subText = isDark ? 'text-gray-300' : 'text-gray-600';

  // Helper functions for CSV table parsing and rendering
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current.trim());
    return result;
  };

  const parseCSV = (csvText) => {
    if (!csvText || !csvText.trim()) return [];
    
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];
    
    return lines.map(line => parseCSVLine(line));
  };

  const extractCSVSections = (textContent) => {
    if (!textContent) return { sections: [], plainText: textContent };
    
    const sections = [];
    const lines = textContent.split('\n');
    
    // Helper function to check if a line looks like CSV
    const isCSVLine = (line) => {
      if (!line || line.trim().length === 0) return false;
      
      const trimmed = line.trim();
      
      // Count commas in the line
      const commaCount = (trimmed.match(/,/g) || []).length;
      
      // A line is likely CSV if it has at least 2 commas (indicating at least 3 columns)
      if (commaCount >= 2) {
        // Try to parse it as CSV to verify
        try {
          const parsed = parseCSVLine(trimmed);
          // Must have at least 2 columns, and at least one non-empty field
          const nonEmptyFields = parsed.filter(f => f && f.trim().length > 0);
          return parsed.length >= 2 && nonEmptyFields.length >= 1;
        } catch {
          return false;
        }
      }
      
      return false;
    };
    
    // Helper function to check if a line is blank/separator
    const isBlankLine = (line) => {
      if (!line || line.trim().length === 0) return true;
      
      const trimmed = line.trim();
      
      // Lines with mostly commas and spaces are considered blank/separators
      if (trimmed.length > 0) {
        // Remove commas and spaces
        const nonCommaSpaceChars = trimmed.replace(/[, ]/g, '');
        
        // If very few non-comma/space characters, it's likely a separator
        // For lines with many commas, if less than 5% are actual content, it's a separator
        if (trimmed.length > 20 && nonCommaSpaceChars.length < trimmed.length * 0.05) {
          return true;
        }
        
        // Lines that are just commas with maybe a few words (like "Visual,,,,,")
        // but have more commas than content are separators
        const commaCount = (trimmed.match(/,/g) || []).length;
        if (commaCount > 10 && nonCommaSpaceChars.length < commaCount * 0.5) {
          return true;
        }
      }
      
      return false;
    };
    
    // First, try to find structured patterns (PDF tables, Excel sheets)
    const pdfTablePattern = /={70,}\nTABLE\s+(\d+)(?::\s+([^\n]+))?\n={70,}\n\n([\s\S]+?)\n\n\[Table Statistics:[^\]]+\]\n={70,}/g;
    const excelSheetPattern = /##\s+Sheet\s+(\d+)(?::\s+([^\n]+))?\n([\s\S]+?)(?:\n\[Sheet Statistics:[^\]]+\])?/g;
    
    const processedRanges = [];
    let match;
    
    // Try PDF table pattern
    while ((match = pdfTablePattern.exec(textContent)) !== null) {
      const tableNum = match[1];
      const title = match[2] ? match[2].trim() : null;
      const csvData = match[3].trim();
      const startIndex = match.index;
      const endIndex = match.index + match[0].length;
      
      if (csvData.length > 0) {
        const rows = parseCSV(csvData);
        if (rows.length > 0 && rows[0].length > 1) {
          sections.push({
            title: title || `Table ${tableNum}`,
            rows,
            startIndex,
            endIndex,
            rawText: csvData
          });
          processedRanges.push([startIndex, endIndex]);
        }
      }
    }
    
    // Try Excel sheet pattern
    while ((match = excelSheetPattern.exec(textContent)) !== null) {
      const sheetNum = match[1];
      const title = match[2] ? match[2].trim() : null;
      const csvData = match[3].trim();
      const startIndex = match.index;
      const endIndex = match.index + match[0].length;
      
      if (csvData.length > 0) {
        const rows = parseCSV(csvData);
        if (rows.length > 0 && rows[0].length > 1) {
          sections.push({
            title: title || `Sheet ${sheetNum}`,
            rows,
            startIndex,
            endIndex,
            rawText: csvData
          });
          processedRanges.push([startIndex, endIndex]);
        }
      }
    }
    
    // Now do smart detection: scan through lines looking for CSV blocks
    let currentCSVBlock = [];
    let currentBlockStartLine = -1;
    let tableNumber = sections.length + 1;
    
    // Calculate line positions in the original text
    let currentPosition = 0;
    const linePositions = lines.map((line, idx) => {
      const pos = currentPosition;
      currentPosition += line.length + 1; // +1 for newline
      return { line, index: idx, position: pos };
    });
    
    for (let i = 0; i < linePositions.length; i++) {
      const { line, position: linePosition } = linePositions[i];
      
      // Check if this line is already part of a processed range
      const lineEnd = linePosition + line.length;
      const isProcessed = processedRanges.some(([start, end]) => 
        (linePosition >= start && linePosition < end) || 
        (lineEnd > start && lineEnd <= end)
      );
      
      if (isProcessed) {
        // Skip lines that are already part of a detected table
        if (currentCSVBlock.length > 0) {
          // Finalize current block before skipping
          const blockText = currentCSVBlock.join('\n');
          const rows = parseCSV(blockText);
          if (rows.length > 0 && rows[0].length > 1) {
            const startIdx = currentBlockStartLine >= 0 
              ? linePositions[currentBlockStartLine].position 
              : linePosition;
            sections.push({
              title: `Table ${tableNumber++}`,
              rows,
              startIndex: startIdx,
              endIndex: linePosition,
              rawText: blockText
            });
          }
          currentCSVBlock = [];
          currentBlockStartLine = -1;
        }
        continue;
      }
      
      if (isCSVLine(line)) {
        // This is a CSV line - add to current block
        if (currentCSVBlock.length === 0) {
          // Start of a new CSV block
          currentBlockStartLine = i;
        }
        currentCSVBlock.push(line);
      } else if (isBlankLine(line)) {
        // Blank line - if we have a CSV block, finalize it and start a new one
        if (currentCSVBlock.length > 0) {
          const blockText = currentCSVBlock.join('\n');
          const rows = parseCSV(blockText);
          if (rows.length > 0 && rows[0].length > 1) {
            const startIdx = currentBlockStartLine >= 0 
              ? linePositions[currentBlockStartLine].position 
              : 0;
            sections.push({
              title: `Table ${tableNumber++}`,
              rows,
              startIndex: startIdx,
              endIndex: linePosition,
              rawText: blockText
            });
          }
          currentCSVBlock = [];
          currentBlockStartLine = -1;
        }
      } else {
        // Non-CSV, non-blank line - check if CSV continues after this line
        // Look ahead 1-2 lines to see if CSV continues
        let csvContinues = false;
        if (i + 1 < linePositions.length) {
          const nextLine = linePositions[i + 1].line;
          if (isCSVLine(nextLine)) {
            csvContinues = true;
          } else if (i + 2 < linePositions.length && isBlankLine(nextLine)) {
            // Check line after blank line
            const lineAfterBlank = linePositions[i + 2].line;
            if (isCSVLine(lineAfterBlank)) {
              csvContinues = true;
            }
          }
        }
        
        if (!csvContinues) {
          // CSV doesn't continue, finalize the block if we have enough lines
          if (currentCSVBlock.length >= 2) {
            const blockText = currentCSVBlock.join('\n');
            const rows = parseCSV(blockText);
            if (rows.length > 0 && rows[0].length > 1) {
              const startIdx = currentBlockStartLine >= 0 
                ? linePositions[currentBlockStartLine].position 
                : 0;
              sections.push({
                title: `Table ${tableNumber++}`,
                rows,
                startIndex: startIdx,
                endIndex: linePosition,
                rawText: blockText
              });
            }
          }
          // Clear the block (whether we saved it or not)
          currentCSVBlock = [];
          currentBlockStartLine = -1;
        }
        // If CSV continues, keep the block and continue to next iteration
      }
    }
    
    // Finalize any remaining CSV block at the end
    if (currentCSVBlock.length > 0) {
      const blockText = currentCSVBlock.join('\n');
      const rows = parseCSV(blockText);
      if (rows.length > 0 && rows[0].length > 1) {
        const startIdx = currentBlockStartLine >= 0 
          ? linePositions[currentBlockStartLine].position 
          : 0;
        sections.push({
          title: `Table ${tableNumber++}`,
          rows,
          startIndex: startIdx,
          endIndex: textContent.length,
          rawText: blockText
        });
      }
    }
    
    // Sort sections by position in text
    sections.sort((a, b) => a.startIndex - b.startIndex);
    
    return { sections, plainText: textContent };
  };

  const renderTableView = (textContent) => {
    const { sections, plainText } = extractCSVSections(textContent);
    
    if (sections.length === 0) {
      // No CSV tables found, show plain text
      return (
        <div className={`rounded-lg border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className={`text-sm mb-2 ${subText}`}>
            No CSV tables detected. Showing plain text view.
          </div>
          <pre className={`text-sm font-mono whitespace-pre-wrap break-words ${textPrimary}`}>
            {plainText}
          </pre>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Render each CSV table */}
        {sections.map((section, idx) => (
          <div key={idx} className={`rounded-lg border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${textPrimary}`}>
              {section.title}
            </h3>
            <div className="overflow-x-auto">
              <table className={`min-w-full border-collapse text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                <thead>
                  {section.rows.length > 0 && (
                    <tr className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      {section.rows[0].map((header, colIdx) => (
                        <th
                          key={colIdx}
                          className={`border px-3 py-2 text-left font-semibold ${
                            isDark ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-700'
                          }`}
                        >
                          {header || `Column ${colIdx + 1}`}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {section.rows.slice(1).map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className={`${
                        rowIdx % 2 === 0
                          ? isDark ? 'bg-gray-800' : 'bg-white'
                          : isDark ? 'bg-gray-750' : 'bg-gray-50'
                      } ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      {section.rows[0].map((_, colIdx) => (
                        <td
                          key={colIdx}
                          className={`border px-3 py-2 ${
                            isDark ? 'border-gray-600' : 'border-gray-300'
                          }`}
                        >
                          {row[colIdx] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={`mt-2 text-xs ${subText}`}>
              {section.rows.length - 1} rows × {section.rows[0]?.length || 0} columns
            </div>
          </div>
        ))}
        
        {/* Show remaining plain text if any */}
        {plainText && sections.length > 0 && (
          <div className={`rounded-lg border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className={`text-sm font-semibold mb-2 ${textPrimary}`}>
              Additional Text Content
            </h3>
            <pre className={`text-sm font-mono whitespace-pre-wrap break-words ${textPrimary}`}>
              {plainText}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={dropZoneRef}
      className={`space-y-6 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />

      {!hideFilters ? (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-600 text-white shadow-md">
                <Paperclip className="h-5 w-5" />
              </div>
              <div>
                <h1 className={`text-2xl font-semibold ${textPrimary}`}>
                  {isGlobalMode
                    ? t('attachments.workspaceTitle', 'Attachment Workspace')
                    : t('attachments.entityTitle', 'Attachments')}
                </h1>
                <p className={`text-sm ${subText}`}>
                  {isGlobalMode
                    ? t(
                        'attachments.workspaceSubtitle',
                        'Monitor, search, and curate every file across tenants in one professional console.'
                      )
                    : t(
                        'attachments.entitySubtitle',
                        'Manage files and URLs associated with this record.'
                      )}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openUploadModal}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Upload className="h-4 w-4" />
              {t('attachments.uploadFile', 'Upload File')}
            </button>
            <button
              type="button"
              onClick={openUrlModal}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <LinkIcon className="h-4 w-4" />
              {t('attachments.addLink', 'Add Link')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openUploadModal}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Upload className="h-4 w-4" />
            {t('attachments.uploadFile', 'Upload File')}
          </button>
          <button
            type="button"
            onClick={openUrlModal}
            className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <LinkIcon className="h-4 w-4" />
            {t('attachments.addLink', 'Add Link')}
          </button>
        </div>
      )}

      {!hideFilters && (
        <>
          {isGlobalMode ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-blue-400">
                      {t('attachments.totalAttachments', 'Total Attachments')}
                    </p>
                    <p className={`mt-2 text-2xl font-semibold ${textPrimary}`}>
                      {stats.totalCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-lg bg-blue-500/10 p-2 text-blue-500">
                    <Layers className="h-5 w-5" />
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-indigo-400">
                      {t('attachments.storageConsumption', 'Storage Consumption')}
                    </p>
                    <p className={`mt-2 text-2xl font-semibold ${textPrimary}`}>
                      {formatBytes(stats.totalSize)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-indigo-500/10 p-2 text-indigo-500">
                    <PieChart className="h-5 w-5" />
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-emerald-400">
                      {t('attachments.documents', 'Documents')}
                    </p>
                    <p className={`mt-2 text-2xl font-semibold ${textPrimary}`}>
                      {(stats.byType.File || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-500">
                    <ListChecks className="h-5 w-5" />
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-amber-400">
                      {t('attachments.images', 'Images')}
                    </p>
                    <p className={`mt-2 text-2xl font-semibold ${textPrimary}`}>
                      {(stats.byType.Image || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-lg bg-amber-500/10 p-2 text-amber-500">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm`}>
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-blue-500/10 p-2 text-blue-500">
                  <Info className="h-5 w-5" />
                </div>
                <div>
                  <h2 className={`text-lg font-medium ${textPrimary}`}>
                    {t('attachments.entityContext', 'Attachment Context')}
                  </h2>
                  <p className={`text-sm ${subText}`}>
                    {t(
                      'attachments.entityContextDescription',
                      'Files uploaded here are automatically linked and visible within this record.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isKnowledgeEntity && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 shadow-sm dark:border-amber-500 dark:bg-amber-500/10 dark:text-amber-200">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-amber-500/20 p-2 text-amber-600 dark:bg-amber-500/30 dark:text-amber-100">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">
                    {t('attachments.knowledgeReadOnlyTitle', 'Managed in Knowledge Center')}
                  </h3>
                  <p className="text-sm">
                    {t(
                      'attachments.knowledgeReadOnlyDescription',
                      'Knowledge article attachments are synchronized from the Knowledge Center and are read-only within this view.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!hideFilters && (
        <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm`}>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-blue-500" />
            <span className={`text-sm font-medium ${textPrimary}`}>
              {t('attachments.filters', 'Filters')}
            </span>
          </div>
          <button
            type="button"
            onClick={() =>
              setFilters({
                search: '',
                entityType: isGlobalMode ? '' : canonicalEntityType || '',
                attachmentType: '',
                source: '',
              })
            }
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {t('common.reset', 'Reset')}
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={filters.search}
              onChange={(event) => handleFilterChange('search', event.target.value)}
              type="text"
              placeholder={t('attachments.searchPlaceholder', 'Search by title, file, or tag')}
              className={`w-full rounded-lg border px-10 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                  : 'border-gray-200 bg-white text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          <select
            value={filters.entityType}
            onChange={(event) => handleFilterChange('entityType', event.target.value)}
            disabled={!isGlobalMode}
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? 'border-gray-700 bg-gray-800 text-gray-100'
                : 'border-gray-200 bg-white text-gray-900'
            } ${!isGlobalMode ? 'opacity-70' : ''}`}
          >
            {entityOptions.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.value === ''
                  ? t('attachments.allEntities', option.label)
                  : option.label}
              </option>
            ))}
          </select>

          <select
            value={filters.attachmentType}
            onChange={(event) =>
              handleFilterChange('attachmentType', event.target.value)
            }
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? 'border-gray-700 bg-gray-800 text-gray-100'
                : 'border-gray-200 bg-white text-gray-900'
            }`}
          >
            <option value="">{t('attachments.allTypes', 'All Attachment Types')}</option>
            <option value="File">{t('attachments.file', 'File')}</option>
            <option value="Image">{t('attachments.image', 'Image')}</option>
            <option value="URL">{t('attachments.url', 'Link')}</option>
          </select>

          <select
            value={filters.source}
            onChange={(event) => handleFilterChange('source', event.target.value)}
            className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
              isDark
                ? 'border-gray-700 bg-gray-800 text-gray-100'
                : 'border-gray-200 bg-white text-gray-900'
            }`}
          >
            <option value="">{t('attachments.allSources', 'All Sources')}</option>
            <option value="attachments">
              {t('attachments.crmSource', 'CRM Uploads')}
            </option>
            <option value="knowledge">
              {t('attachments.knowledgeSource', 'Knowledge Center')}
            </option>
          </select>
        </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
          <ShieldAlert className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedAttachments.size > 0 && (
        <div className={`rounded-xl border ${cardBackground} p-4 shadow-sm border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${textPrimary}`}>
                {selectedAttachments.size} attachment(s) selected
              </span>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Clear selection
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setBulkTagModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-purple-700"
              >
                <Tag className="h-4 w-4" />
                Manage Tags
              </button>
              <button
                type="button"
                onClick={handleBulkExport}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                Export as ZIP
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`relative rounded-xl border ${cardBackground} shadow-sm ${
          viewMode === 'gallery' ? 'p-6' : ''
        } ${isDragOver ? 'ring-4 ring-blue-500 ring-opacity-50' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-blue-500/10 backdrop-blur-sm">
            <div className="rounded-xl bg-white dark:bg-gray-800 p-8 shadow-2xl border-4 border-dashed border-blue-500">
              <Upload className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <p className="text-xl font-semibold text-blue-600 dark:text-blue-400">
                {t('attachments.dropFileHere', 'Drop file here to upload')}
              </p>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`text-sm ${subText}`}>
              {t('attachments.showingCount', 'Showing')} {displayAttachments.length}{' '}
              {t('attachments.ofTotal', 'of')} {totalResults || attachments.length}{' '}
              {t('attachments.attachmentsLabel', 'attachments')}
            </div>
            {displayAttachments.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllAttachments}
                  disabled={isAllSelected}
                  className={`text-xs px-2 py-1 rounded border ${
                    isDark
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50'
                  }`}
                >
                  Select All
                </button>
                {selectedAttachments.size > 0 && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className={`text-xs px-2 py-1 rounded border ${
                      isDark
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Clear ({selectedAttachments.size})
                  </button>
                )}
              </div>
            )}
          </div>
          {isGlobalMode && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium ${
                  viewMode === 'table'
                    ? 'bg-blue-600 text-white shadow'
                    : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <ListChecks className="h-4 w-4" />
                {t('attachments.tableView', 'Table')}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('gallery')}
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium ${
                  viewMode === 'gallery'
                    ? 'bg-blue-600 text-white shadow'
                    : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Layers className="h-4 w-4" />
                {t('attachments.gridView', 'Gallery')}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-sm text-blue-600">
              {t('attachments.loading', 'Loading attachments...')}
            </span>
          </div>
        ) : displayAttachments.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Paperclip className="h-10 w-10 text-gray-400" />
            <div className="text-base font-medium text-gray-600 dark:text-gray-300">
              {t('attachments.noResultsTitle', 'No attachments match your filters')}
            </div>
            <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
              {t(
                'attachments.noResultsDescription',
                'Adjust your filters or upload a new file to start curating your attachment workspace.'
              )}
            </p>
          </div>
        ) : hideFilters ? (
          // Card list view for entity pages (when hideFilters is true)
          <div className="space-y-3 pt-4">
            {displayAttachments.map((attachment) => (
              <div
                key={attachment.AttachmentID}
                className={`rounded-lg border p-4 transition-all duration-150 hover:shadow-md ${
                  isAttachmentSelected(attachment.AttachmentID)
                    ? 'border-blue-500 ring-2 ring-blue-500 dark:border-blue-400 dark:ring-blue-400'
                    : isAttachmentReadOnly(attachment)
                    ? isDark
                      ? 'border-amber-500/50 bg-gray-800/50'
                      : 'border-amber-300 bg-amber-50/30'
                    : isDark
                      ? 'border-gray-700 bg-gray-800/30'
                      : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={isAttachmentSelected(attachment.AttachmentID)}
                      onChange={() => toggleAttachmentSelection(attachment.AttachmentID)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  {/* File Icon */}
                  <div className="flex-shrink-0 mt-1">{getFileIcon(attachment)}</div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title */}
                    <button
                      type="button"
                      onClick={() => openPreview(attachment)}
                      className={`block text-left text-base font-semibold mb-1 ${
                        isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                      title={attachment.Title || attachment.OriginalFileName || 'Untitled'}
                    >
                      {attachment.Title || attachment.OriginalFileName || 'Untitled'}
                    </button>

                    {/* Description */}
                    {attachment.Description && (
                      <p className={`text-sm mb-2 ${subText}`}>
                        {attachment.Description}
                      </p>
                    )}

                    {/* URL Link (if applicable) */}
                    {attachment.AttachmentType === 'URL' && attachment.FilePath && (
                      <a
                        href={attachment.FilePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 text-sm mb-2 ${
                          isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                        }`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {attachment.FilePath}
                      </a>
                    )}

                    {/* Metadata Row */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {attachment.UploadedByName || 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(attachment.CreatedAt)}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-700 dark:text-gray-300">
                        {formatBytes(attachment.FileSize)}
                      </span>
                    </div>

                    {/* Tags */}
                    <div className="mb-2">
                      <TagService
                        initialTags={attachment.Tags || []}
                        readonly={isAttachmentReadOnly(attachment)}
                        maxTags={10}
                        onChange={(newTags) => {
                          handleAttachmentTagChange(attachment.AttachmentID, newTags);
                        }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => openPreview(attachment)}
                      className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      title={t('common.view', 'View')}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(attachment)}
                      className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      title={t('common.download', 'Download')}
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {!isAttachmentReadOnly(attachment) && (
                      <>
                        <button
                          type="button"
                          onClick={() => openEditModal(attachment)}
                          className="rounded-md bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/30"
                          title={t('common.edit', 'Edit')}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(attachment)}
                          className="rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-300 dark:hover:bg-red-500/30"
                          title={t('common.delete', 'Delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'gallery' ? (
          <div className="grid gap-4 pt-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayAttachments.map((attachment) => {
              const isImage = isImageAttachment(attachment);
              const attachmentId = attachment.AttachmentID;
              const imageUrl = isImage ? imageUrls[attachmentId] : null;
              const isLoadingImage = isImage ? loadingImages[attachmentId] : false;
              const imageFailed = isImage ? failedImages[attachmentId] : null;
              const isNotFound = imageFailed === 'not_found';

              // Load image URL if it's an image and not loaded yet, and hasn't failed
              if (isImage && !imageUrl && !isLoadingImage && !imageFailed) {
                // Trigger image load asynchronously
                loadImageUrl(attachment).catch((err) => {
                  console.error('Failed to preload image:', err);
                });
              }

              return (
                <div
                  key={attachment.AttachmentID}
                  className={`relative rounded-lg border overflow-hidden transition-all duration-150 hover:shadow-md ${
                    isAttachmentSelected(attachment.AttachmentID)
                      ? 'border-blue-500 ring-2 ring-blue-500 dark:border-blue-400 dark:ring-blue-400'
                      : isAttachmentReadOnly(attachment)
                      ? 'border-amber-300 dark:border-amber-500/50'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="absolute top-2 right-2 z-10">
                    <input
                      type="checkbox"
                      checked={isAttachmentSelected(attachment.AttachmentID)}
                      onChange={() => toggleAttachmentSelection(attachment.AttachmentID)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 bg-white/90 dark:bg-gray-800/90"
                    />
                  </div>

                  {/* Image Preview Section */}
                  {isImage && (
                    <div 
                      className="relative w-full h-48 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer overflow-hidden group"
                      onClick={() => openPreview(attachment)}
                    >
                      {isNotFound ? (
                        <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
                          <ShieldAlert className="h-10 w-10 text-red-500 dark:text-red-400" />
                          <span className="text-sm font-medium text-red-600 dark:text-red-400">File not found</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">The file is no longer available on the server</span>
                        </div>
                      ) : isLoadingImage ? (
                        <div className="flex flex-col items-center justify-center gap-2">
                          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                          <span className="text-xs text-gray-500 dark:text-gray-400">Loading...</span>
                        </div>
                      ) : imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={attachment.Title || attachment.OriginalFileName || 'Image'}
                          className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                          onError={(e) => {
                            // Fallback to icon if image fails to load
                            const parent = e.target.parentElement;
                            if (parent) {
                              parent.innerHTML = `
                                <div class="flex flex-col items-center justify-center gap-2 h-full">
                                  <svg class="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span class="text-xs text-gray-500 dark:text-gray-400">Preview unavailable</span>
                                </div>
                              `;
                            }
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-2">
                          {getFileIcon(attachment)}
                          <span className="text-xs text-gray-500 dark:text-gray-400">Preview unavailable</span>
                        </div>
                      )}
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
                    </div>
                  )}

                  {/* File Type Icon Preview Section for Non-Image Files */}
                  {!isImage && attachment.AttachmentType !== 'URL' && (
                    <FileTypePreview 
                      attachment={attachment}
                      onPreviewClick={() => openPreview(attachment)}
                    />
                  )}

                  {/* URL Preview Section */}
                  {!isImage && attachment.AttachmentType === 'URL' && (
                    <div 
                      className="relative w-full h-48 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer overflow-hidden group"
                      onClick={() => openPreview(attachment)}
                    >
                      <div className="flex flex-col items-center justify-center gap-2">
                        <ExternalLink className="h-16 w-16 text-blue-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          External Link
                        </span>
                      </div>
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
                    </div>
                  )}

                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => openPreview(attachment)}
                      className={`block text-left text-sm font-medium ${
                        isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                      }`}
                      title={attachment.Title || attachment.OriginalFileName || 'Untitled'}
                    >
                      {attachment.Title || attachment.OriginalFileName || 'Untitled'}
                    </button>
                    {attachment.Description && (
                      <p className={`mt-1 truncate text-xs ${subText}`}>
                        {attachment.Description}
                      </p>
                    )}
                    <div className="mt-3 space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <Tag className="h-3 w-3 text-gray-400" />
                        <span>{resolveEntityLabel(attachment.EntityType)}</span>
                        {attachment.EntityID && (
                          <RouterLink
                            to={resolveEntityUrl(attachment.EntityType, attachment.EntityID) || '#'}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            #{attachment.EntityID}
                          </RouterLink>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-gray-400" />
                        <span>{formatDate(attachment.CreatedAt)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-gray-400" />
                        <span>{attachment.UploadedByName || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-emerald-500">
                        <FileIcon className="h-3 w-3" />
                        <span>{formatBytes(attachment.FileSize)}</span>
                      </div>
                    </div>
                  </div>
                    </div>
                  </div>
                <div className="mt-4 space-y-3">
                  {/* Tags with TagService */}
                  <div>
                    <TagService
                      initialTags={attachment.Tags || []}
                      readonly={isAttachmentReadOnly(attachment)}
                      maxTags={10}
                      onChange={(newTags) => {
                        handleAttachmentTagChange(attachment.AttachmentID, newTags);
                      }}
                    />
                  </div>

                  {/* Source label and actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {getAttachmentSourceLabel(attachment) && (
                        <span className="rounded-full bg-purple-100 px-2 py-0.5 text-purple-600 dark:bg-purple-500/20 dark:text-purple-200">
                          {getAttachmentSourceLabel(attachment)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openPreview(attachment)}
                        className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                      >
                        <ImageIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownload(attachment)}
                        className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {!isAttachmentReadOnly(attachment) && (
                        <button
                          type="button"
                          onClick={() => openEditModal(attachment)}
                          className="rounded-md bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-200 dark:hover:bg-blue-500/30"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={tableHeaderClass}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isSomeSelected;
                      }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllAttachments();
                        } else {
                          clearSelection();
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    {t('attachments.file', 'File')}
                  </th>
                  {!hideFilters && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('attachments.entity', 'Entity')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('attachments.type', 'Type')}
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    {t('attachments.size', 'Size')}
                  </th>
                  {!hideFilters && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('attachments.uploaded', 'Uploaded')}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                    {t('attachments.tags', 'Tags')}
                  </th>
                  {!hideFilters && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                      Text Content
                    </th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">
                    {t('attachments.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {displayAttachments.map((attachment) => (
                  <tr
                    key={attachment.AttachmentID}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={isAttachmentSelected(attachment.AttachmentID)}
                        onChange={() => toggleAttachmentSelection(attachment.AttachmentID)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">{getFileIcon(attachment)}</div>
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => openPreview(attachment)}
                            className={`block text-left text-sm font-semibold ${
                              isDark
                                ? 'text-blue-300 hover:text-blue-200'
                                : 'text-blue-600 hover:text-blue-800'
                            }`}
                            title={attachment.Title || attachment.OriginalFileName || 'Untitled'}
                          >
                            {attachment.Title || attachment.OriginalFileName || 'Untitled'}
                          </button>
                          {!hideFilters && attachment.Description && (
                            <p className={`mt-1 text-xs ${subText}`}>
                              {attachment.Description}
                            </p>
                          )}
                          {!hideFilters && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-700 dark:text-gray-200">
                                {attachment.AttachmentType}
                              </span>
                              {getAttachmentSourceLabel(attachment) && (
                                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-purple-600 dark:bg-purple-500/20 dark:text-purple-200">
                                  {getAttachmentSourceLabel(attachment)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {!hideFilters && (
                      <>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {resolveEntityLabel(attachment.EntityType)}
                            </span>
                            {attachment.EntityID && (
                              <RouterLink
                                to={resolveEntityUrl(attachment.EntityType, attachment.EntityID) || '#'}
                                className={`text-xs ${
                                  isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                                }`}
                              >
                                #{attachment.EntityID}
                              </RouterLink>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                          {attachment.AttachmentType}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {formatBytes(attachment.FileSize)}
                    </td>
                    {!hideFilters && (
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex flex-col">
                          <span>{attachment.UploadedByName || '-'}</span>
                          <span className="text-xs text-gray-400">
                            {formatDate(attachment.CreatedAt)}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                      <TagService
                        initialTags={attachment.Tags || []}
                        readonly={isAttachmentReadOnly(attachment)}
                        maxTags={10}
                        onChange={(newTags) => {
                          handleAttachmentTagChange(attachment.AttachmentID, newTags);
                        }}
                      />
                    </td>
                    {!hideFilters && attachment.AttachmentType !== 'URL' && (
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">
                        {attachment.TextContent ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-200">
                              <FileText className="h-3 w-3" />
                              {attachment.TextContent.length > 0 ? `${(attachment.TextContent.length / 1000).toFixed(1)}k chars` : 'Extracted'}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setViewingTextContent(attachment);
                                setTextViewMode('plain'); // Reset to plain view
                                setTextContentModalOpen(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              title="View extracted text"
                            >
                              View
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Not extracted</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-4 text-right text-sm">
                      <div className="flex justify-end gap-2">
                        {hideFilters ? (
                          // Simplified view: only delete button
                          !isAttachmentReadOnly(attachment) && (
                            <button
                              type="button"
                              onClick={() => handleDelete(attachment)}
                              className="rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-200 dark:hover:bg-red-500/30"
                              title={t('common.delete', 'Delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )
                        ) : (
                          // Full view: all actions
                          <>
                            <button
                              type="button"
                              onClick={() => openPreview(attachment)}
                              className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                              <ImageIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownload(attachment)}
                              className="rounded-md bg-gray-100 p-2 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            {attachment.AttachmentType !== 'URL' && (
                              <>
                                {attachment.TextContent && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setViewingTextContent(attachment);
                                      setTextViewMode('plain'); // Reset to plain view
                                      setTextContentModalOpen(true);
                                    }}
                                    className="rounded-md bg-purple-100 p-2 text-purple-600 hover:bg-purple-200 dark:bg-purple-500/20 dark:text-purple-200 dark:hover:bg-purple-500/30"
                                    title="View extracted text"
                                  >
                                    <FileText className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleExtractText(attachment)}
                                  disabled={extractingText}
                                  className="rounded-md bg-green-100 p-2 text-green-600 hover:bg-green-200 dark:bg-green-500/20 dark:text-green-200 dark:hover:bg-green-500/30 disabled:opacity-50"
                                  title="Extract text from file"
                                >
                                  {extractingText ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-4 w-4" />
                                  )}
                                </button>
                              </>
                            )}
                            {!isAttachmentReadOnly(attachment) && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openEditModal(attachment)}
                                  className="rounded-md bg-blue-100 p-2 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-200 dark:hover:bg-blue-500/30"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(attachment)}
                                  className="rounded-md bg-red-100 p-2 text-red-600 hover:bg-red-200 dark:bg-red-500/20 dark:text-red-200 dark:hover:bg-red-500/30"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isGlobalMode && pageCount > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
            <div>
              {t('attachments.pageLabel', 'Page')} {page} {t('attachments.ofLabel', 'of')}{' '}
              {pageCount}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(-1)}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('attachments.previous', 'Previous')}
              </button>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={page >= pageCount}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {t('attachments.next', 'Next')}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {uploadModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={(e) => {
            // Close modal only if clicking the backdrop
            if (e.target === e.currentTarget) {
              setUploadModalOpen(false);
              setUploadForm((prev) => ({ ...prev, file: null }));
            }
          }}
        >
          <div 
            className={`w-full max-w-xl rounded-2xl border ${cardBackground} p-6 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className={`text-xl font-semibold ${textPrimary}`}>
                  {t('attachments.uploadFile', 'Upload File')}
                </h2>
                <p className={`text-sm ${subText}`}>
                  {t('attachments.uploadDescription', 'Attach a document, image, or resource to the selected entity.')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setUploadModalOpen(false);
                  setUploadForm((prev) => ({ ...prev, file: null }));
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium">{t('attachments.file', 'File')}</label>
                <div
                  className={`mt-2 flex items-center justify-between rounded-lg border px-3 py-2 ${
                    isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <span className="text-sm">
                    {uploadForm.file?.name || t('attachments.noFileSelected', 'No file selected')}
                  </span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-md bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                  >
                    {t('attachments.browse', 'Browse')}
                  </button>
                </div>
              </div>

              {isGlobalMode && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">
                      {t('attachments.entityType', 'Entity Type')}
                    </label>
                    <select
                      value={uploadForm.entityType}
                      onChange={(event) =>
                        setUploadForm((prev) => ({
                          ...prev,
                          entityType: canonicalizeEntityTypeValue(event.target.value) || '',
                        }))
                      }
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white text-gray-900'
                      }`}
                    >
                      <option value="">
                        {t('attachments.selectEntityType', 'Select entity type')}
                      </option>
                      {entityOptions
                        .filter((option) => option.value !== '')
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {t('attachments.entityId', 'Entity ID')}
                    </label>
                    <input
                      type="text"
                      value={uploadForm.entityId}
                      onChange={(event) =>
                        setUploadForm((prev) => ({
                          ...prev,
                          entityId: event.target.value,
                        }))
                      }
                      placeholder={t('attachments.entityIdPlaceholder', 'Leave blank for Global')}
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                          : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.title', 'Title')}
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.description', 'Description')}
                </label>
                <textarea
                  rows={3}
                  value={uploadForm.description}
                  onChange={(event) =>
                    setUploadForm((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.tags', 'Tags')}
                </label>
                <div className="mt-1">
                  <TagService
                    initialTags={uploadForm.tags ? (typeof uploadForm.tags === 'string' ? uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean) : uploadForm.tags) : []}
                    maxTags={10}
                    onChange={(newTags) => {
                      setUploadForm((prev) => ({ ...prev, tags: newTags.join(', ') }));
                    }}
                    placeholder={t('attachments.tagsPlaceholder', 'Add tags...')}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setUploadModalOpen(false);
                  setUploadForm((prev) => ({ ...prev, file: null }));
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleFileUpload}
                disabled={!uploadForm.file || loading}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading && <RefreshCw className="h-4 w-4 animate-spin" />}
                {loading
                  ? t('attachments.uploading', 'Uploading...')
                  : t('attachments.upload', 'Upload')}
              </button>
            </div>
          </div>
        </div>
      )}

      {urlModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${cardBackground} p-6 shadow-xl`}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className={`text-xl font-semibold ${textPrimary}`}>
                  {t('attachments.addLink', 'Add Link')}
                </h2>
                <p className={`text-sm ${subText}`}>
                  {t(
                    'attachments.addLinkDescription',
                    'Reference external resources by attaching a contextual link.'
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUrlModalOpen(false)}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium">URL</label>
                <input
                  type="url"
                  value={urlForm.url}
                  onChange={(event) =>
                    setUrlForm((prev) => ({ ...prev, url: event.target.value }))
                  }
                  placeholder="https://example.com/document"
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {isGlobalMode && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">
                      {t('attachments.entityType', 'Entity Type')}
                    </label>
                    <select
                      value={urlForm.entityType}
                      onChange={(event) =>
                        setUrlForm((prev) => ({
                          ...prev,
                          entityType: canonicalizeEntityTypeValue(event.target.value) || '',
                        }))
                      }
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white text-gray-900'
                      }`}
                    >
                      <option value="">
                        {t('attachments.selectEntityType', 'Select entity type')}
                      </option>
                      {entityOptions
                        .filter((option) => option.value !== '')
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {t('attachments.entityId', 'Entity ID')}
                    </label>
                    <input
                      type="text"
                      value={urlForm.entityId}
                      onChange={(event) =>
                        setUrlForm((prev) => ({ ...prev, entityId: event.target.value }))
                      }
                      placeholder={t('attachments.entityIdPlaceholder', 'Leave blank for Global')}
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                          : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.title', 'Title')}
                </label>
                <input
                  type="text"
                  value={urlForm.title}
                  onChange={(event) =>
                    setUrlForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.description', 'Description')}
                </label>
                <textarea
                  rows={3}
                  value={urlForm.description}
                  onChange={(event) =>
                    setUrlForm((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.tags', 'Tags')}
                </label>
                <input
                  type="text"
                  value={urlForm.tags}
                  onChange={(event) =>
                    setUrlForm((prev) => ({ ...prev, tags: event.target.value }))
                  }
                  placeholder={t('attachments.tagsPlaceholder', 'Comma separated')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                      : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setUrlModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleUrlSubmit}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700"
              >
                <LinkIcon className="h-4 w-4" />
                {t('attachments.addLink', 'Add Link')}
              </button>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && editingAttachment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${cardBackground} p-6 shadow-xl`}>
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-semibold ${textPrimary}`}>
                {t('attachments.editAttachment', 'Edit Attachment')}
              </h2>
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {t('attachments.title', 'Title')}
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.description', 'Description')}
                </label>
                <textarea
                  rows={3}
                  value={editForm.description}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark
                      ? 'border-gray-700 bg-gray-800 text-gray-100'
                      : 'border-gray-200 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  {t('attachments.tags', 'Tags')}
                </label>
                <div className="mt-1">
                  <TagService
                    initialTags={editForm.tags ? (typeof editForm.tags === 'string' ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean) : editForm.tags) : []}
                    maxTags={10}
                    onChange={(newTags) => {
                      setEditForm((prev) => ({ ...prev, tags: newTags.join(', ') }));
                    }}
                  />
                </div>
              </div>

              {/* TextContent field - for file/image attachments, Admin/Manager only */}
              {editingAttachment.AttachmentType !== 'URL' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">
                      Extracted Text Content (Admin/Manager)
                    </label>
                    <button
                      type="button"
                      onClick={() => handleExtractText(editingAttachment)}
                      disabled={extractingText}
                      className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-500/20 dark:text-blue-200 dark:hover:bg-blue-500/30 disabled:opacity-50"
                    >
                      {extractingText ? 'Extracting...' : 'Re-extract from File'}
                    </button>
                  </div>
                  <textarea
                    rows={8}
                    value={editForm.textContent}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        textContent: event.target.value,
                      }))
                    }
                    placeholder="Extracted text content from PDF, images, etc. Click 'Re-extract from File' to regenerate."
                    className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 ${
                      isDark
                        ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-400'
                        : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400'
                    }`}
                  />
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {editForm.textContent.length} characters
                  </div>
                </div>
              )}

              {editingAttachment.AttachmentType === 'URL' && (
                <>
                  <div>
                    <label className="text-sm font-medium">URL</label>
                    <input
                      type="text"
                      value={editForm.url}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, url: event.target.value }))
                      }
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {t('attachments.urlTitle', 'URL Title')}
                    </label>
                    <input
                      type="text"
                      value={editForm.urlTitle}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          urlTitle: event.target.value,
                        }))
                      }
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      {t('attachments.urlDescription', 'URL Description')}
                    </label>
                    <textarea
                      rows={2}
                      value={editForm.urlDescription}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          urlDescription: event.target.value,
                        }))
                      }
                      className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
                        isDark
                          ? 'border-gray-700 bg-gray-800 text-gray-100'
                          : 'border-gray-200 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleEditSave}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700"
              >
                <Save className="h-4 w-4" />
                {t('common.saveChanges', 'Save changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text Content View Modal */}
      {textContentModalOpen && viewingTextContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-4xl rounded-2xl border ${cardBackground} p-6 shadow-xl max-h-[90vh] flex flex-col`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className={`text-lg font-semibold ${textPrimary}`}>
                  Extracted Text Content
                </h2>
                <p className={`text-sm ${subText} mt-1`}>
                  {viewingTextContent.OriginalFileName || viewingTextContent.FileName || 'Attachment'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                {viewingTextContent.TextContent && (
                  <div className={`flex items-center gap-1 rounded-lg border p-1 ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                    <button
                      type="button"
                      onClick={() => setTextViewMode('plain')}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                        textContentViewMode === 'plain'
                          ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                          : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Plain Text View"
                    >
                      <AlignLeft className="h-3.5 w-3.5 inline mr-1" />
                      Plain
                    </button>
                    <button
                      type="button"
                      onClick={() => setTextViewMode('table')}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                        textContentViewMode === 'table'
                          ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                          : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Table View (CSV)"
                    >
                      <Table className="h-3.5 w-3.5 inline mr-1" />
                      Table
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setTextContentModalOpen(false);
                    setViewingTextContent(null);
                    setTextViewMode('plain'); // Reset view mode
                  }}
                  className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {viewingTextContent.TextContent ? (
                textContentViewMode === 'table' ? (
                  // Table View
                  renderTableView(viewingTextContent.TextContent)
                ) : (
                  // Plain Text View
                  <div className={`rounded-lg border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                    <pre className={`text-sm font-mono whitespace-pre-wrap break-words ${textPrimary}`}>
                      {viewingTextContent.TextContent}
                    </pre>
                    <div className={`mt-4 text-xs ${subText}`}>
                      {viewingTextContent.TextContent.length.toLocaleString()} characters
                    </div>
                  </div>
                )
              ) : (
                <div className={`rounded-lg border p-8 text-center ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className={subText}>No text content extracted yet.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setTextContentModalOpen(false);
                      handleExtractText(viewingTextContent);
                    }}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700"
                  >
                    <Sparkles className="h-4 w-4" />
                    Extract Text Now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Tag Management Modal */}
      {bulkTagModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className={`w-full max-w-lg rounded-2xl border ${cardBackground} p-6 shadow-xl`}>
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-semibold ${textPrimary}`}>
                Manage Tags for {selectedAttachments.size} Attachment(s)
              </h2>
              <button
                type="button"
                onClick={() => {
                  if (!loading) {
                    setBulkTagModalOpen(false);
                    setBulkTags([]);
                    setError(null);
                  }
                }}
                disabled={loading}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {error && (
                <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                  <ShieldAlert className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
              <div>
                <label className="text-sm font-medium">
                  {t('attachments.tags', 'Tags')}
                </label>
                <div className="mt-1">
                  <TagService
                    key="bulk-tag-service" // Stable key to prevent re-mounting
                    initialTags={bulkTags}
                    maxTags={20}
                    onChange={(newTags) => {
                      setBulkTags(newTags);
                      // Clear error when tags change
                      if (error) setError(null);
                    }}
                    placeholder={t('attachments.tagsPlaceholder', 'Add tags...')}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  These tags will be added to all selected attachments (merged with existing tags)
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!loading) {
                    setBulkTagModalOpen(false);
                    setBulkTags([]);
                    setError(null);
                  }
                }}
                disabled={loading}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleBulkTagUpdate}
                disabled={loading || bulkTags.length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Tag className="h-4 w-4" />
                    Update Tags
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {imageModalOpen && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="relative max-h-[90vh] w-full max-w-4xl rounded-2xl bg-white/90 p-6 shadow-2xl backdrop-blur dark:bg-gray-900/90">
            <button
              type="button"
              onClick={() => {
                setImageModalOpen(false);
                setSelectedImage(null);
              }}
              className="absolute right-4 top-4 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedImage.Title || selectedImage.OriginalFileName || 'Image'}
              </h3>
              <div className="flex justify-center">
                {failedImages[selectedImage.AttachmentID] === 'not_found' ? (
                  <div className="flex h-80 w-full flex-col items-center justify-center gap-4 rounded-lg bg-gray-100 dark:bg-gray-800 p-8 text-center">
                    <ShieldAlert className="h-16 w-16 text-red-500 dark:text-red-400" />
                    <div className="space-y-2">
                      <h4 className="text-lg font-semibold text-red-600 dark:text-red-400">File not found</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        The file is no longer available on the server.
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">
                        This may happen if the file was deleted or moved.
                      </p>
                    </div>
                  </div>
                ) : loadingImages[selectedImage.AttachmentID] ? (
                  <div className="flex h-80 w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                    <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                  </div>
                ) : imageUrls[selectedImage.AttachmentID] ? (
                  <img
                    src={imageUrls[selectedImage.AttachmentID]}
                    alt={selectedImage.Title || selectedImage.OriginalFileName || 'Image'}
                    className="max-h-[65vh] rounded-lg object-contain"
                    onError={(e) => {
                      // If image fails to load, show error
                      const parent = e.target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="flex h-80 w-full flex-col items-center justify-center gap-4 rounded-lg bg-gray-100 dark:bg-gray-800 p-8 text-center">
                            <svg class="h-16 w-16 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div class="space-y-2">
                              <h4 class="text-lg font-semibold text-red-600 dark:text-red-400">Failed to load image</h4>
                              <p class="text-sm text-gray-600 dark:text-gray-400">The image could not be displayed.</p>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                ) : (
                  <div className="flex h-80 w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                    <div className="text-center">
                      <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Preview unavailable</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-blue-500" />
                  <span>{resolveEntityLabel(selectedImage.EntityType)}</span>
                  {selectedImage.EntityID && (
                    <RouterLink
                      to={resolveEntityUrl(selectedImage.EntityType, selectedImage.EntityID) || '#'}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      #{selectedImage.EntityID}
                    </RouterLink>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-500" />
                  <span>{selectedImage.UploadedByName || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span>{formatDate(selectedImage.CreatedAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileIcon className="h-4 w-4 text-blue-500" />
                  <span>{formatBytes(selectedImage.FileSize)}</span>
                </div>
                {selectedImage.Tags?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag className="h-4 w-4 text-blue-500" />
                    {selectedImage.Tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-600 dark:bg-blue-500/20 dark:text-blue-100"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleDownload(selectedImage)}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700"
                >
                  <Download className="h-4 w-4" />
                  {t('attachments.download', 'Download')}
                </button>
                {!isAttachmentReadOnly(selectedImage) && (
                  <button
                    type="button"
                    onClick={() => {
                      setImageModalOpen(false);
                      openEditModal(selectedImage);
                    }}
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    <Edit2 className="h-4 w-4" />
                    {t('attachments.edit', 'Edit')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentWorkspace;

